import {
  Modal,
  Stack,
  Group,
  Text,
  Badge,
  Code,
  Anchor,
  CopyButton,
  ActionIcon,
  Tooltip,
  Divider,
} from "@mantine/core";
import { IconCopy, IconCheck } from "@tabler/icons-react";
import { HeraldTask } from "../types";

interface HeraldTaskDetailsModalProps {
  task: HeraldTask | null;
  opened: boolean;
  onClose: () => void;
}

const STATUS_COLOR: Record<HeraldTask["status"], string> = {
  running: "blue",
  waiting_for_input: "yellow",
  succeeded: "teal",
  failed: "red",
  cancelled: "gray",
};

function formatDate(unixSeconds: number): string {
  if (!unixSeconds) return "—";
  return new Date(unixSeconds * 1000).toLocaleString();
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Text size="xs" color="dimmed" weight={600} tt="uppercase">
        {label}
      </Text>
      <div>{children}</div>
    </div>
  );
}

export function HeraldTaskDetailsModal({
  task,
  opened,
  onClose,
}: HeraldTaskDetailsModalProps) {
  if (!task) return null;

  return (
    <Modal opened={opened} onClose={onClose} title="Task details" size="lg">
      <Stack spacing="md">
        <Group position="apart" align="flex-start">
          <div>
            <Text weight={600} size="lg">
              {task.repo}
            </Text>
            <Group spacing={6} mt={4}>
              <Code color="gray">{task.task_id}</Code>
              <CopyButton value={task.task_id}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? "Copied" : "Copy task ID"}>
                    <ActionIcon size="sm" onClick={copy}>
                      {copied ? (
                        <IconCheck size={14} />
                      ) : (
                        <IconCopy size={14} />
                      )}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          </div>
          <Badge color={STATUS_COLOR[task.status] || "gray"} size="lg">
            {task.status.replace(/_/g, " ")}
          </Badge>
        </Group>

        <Divider />

        <Field label="Prompt">
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {task.prompt || "—"}
          </Text>
        </Field>

        {task.status === "failed" && task.failure_reason && (
          <Field label="Failure reason">
            <Text size="sm" color="red">
              {task.failure_reason}
            </Text>
          </Field>
        )}

        <Group grow align="flex-start">
          <Field label="Triggered by">
            <Text size="sm">{task.triggered_by || "—"}</Text>
          </Field>
          <Field label="Created">
            <Text size="sm">{formatDate(task.created_at)}</Text>
          </Field>
          {task.cleaned_at > 0 && (
            <Field label="Cleaned up">
              <Text size="sm">{formatDate(task.cleaned_at)}</Text>
            </Field>
          )}
        </Group>

        <Group grow align="flex-start">
          <Field label="GitHub repo">
            <Text size="sm">{task.github_repo || "—"}</Text>
          </Field>
          <Field label="Branch">
            <Code>{task.branch || "—"}</Code>
          </Field>
          <Field label="Base branch">
            <Code>{task.base_branch || "—"}</Code>
          </Field>
        </Group>

        <Group grow align="flex-start">
          <Field label="Preview">
            {task.preview_url ? (
              <Anchor href={task.preview_url} target="_blank" size="sm">
                {task.preview_url}
              </Anchor>
            ) : (
              <Text size="sm" color="dimmed">
                —
              </Text>
            )}
          </Field>
          <Field label="Pull request">
            {task.pr_url ? (
              <Anchor href={task.pr_url} target="_blank" size="sm">
                {task.pr_url}
              </Anchor>
            ) : (
              <Text size="sm" color="dimmed">
                —
              </Text>
            )}
          </Field>
        </Group>
      </Stack>
    </Modal>
  );
}
